import React, { useCallback, useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import duration, { Duration } from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import useSWR from 'swr'

import { Avatar, Button } from '../atoms'
import { useJobs } from '../hooks/useJobs'

import { JobTabs, StudyCardProps } from './StudyCard.types'

import { recipeLinkFromSource, useRuns, useWindowDimensions } from '../hooks'

import './Card.scss'

import { CloudJob, JobStatusEnum, Run } from '@pollination-solutions/pollination-sdk'
import { FolderOutlined, InfoCircleOutlined, 
  LoadingOutlined, PartitionOutlined, SettingOutlined } from '@ant-design/icons'
import { Clock, Cpu, InfoCircle, Trash, XOctagon, Recycle } from 'react-bootstrap-icons'
import { Toaster, toast } from 'react-hot-toast'

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(timezone)

const tmz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone?.toString()

const formatDuration = (duration: Duration): string => {
  return `${duration.days() > 0 ? `${duration.days()} day${duration.days() !== 1 ? 's' : ''} and ` : ''} ${duration.format('HH:mm:ss')}`
}

interface HoverObject {
  account: boolean
  project: boolean

  // Click
  details: boolean
  workspace: boolean
  debug: boolean
  // Over
  detailsOver: boolean
  workspaceOver: boolean
  debugOver: boolean

  author: boolean
  recipe: boolean
  info: boolean

  settings: boolean
}

export const StudyCard: React.FC<StudyCardProps> = ({
  projectName,
  projectOwner,
  study: _study,
  style = {},
  authUser,
  client,
  enableClick,
  getTab = (tab: string) => {},
  defaultTab = '',
  enableLink = false,
  canWrite = false,
  getValue = (job: CloudJob) => {},
  interval = 5000,
}) => {

  /**
   * Fetch hooks
   */
  const { fetchJob, getDuration, statusMap, deleteJob, cancelJob, retryFailedRuns } = useJobs(client)
  const { listRuns, getTrigger } = useRuns(client)
  const { width } = useWindowDimensions()

  const [seeDescription, setSeeDescription] = useState(false)
  const [seeAction, setSeeAction] = useState(false)
  const [isReady, setIsReady] = useState<boolean>(false)
  const [retryLoading, setRetryLoading] = useState<boolean>(false)
  const [stopRefresh, setStopRefresh] = useState<boolean>(false)

  /**
   * From job to run
   */
  const { data: study, mutate } = useSWR(
    authUser && _study ? [projectOwner, projectName, _study.id] : undefined,
    fetchJob,
    {
      revalidateOnFocus: !stopRefresh,
      refreshWhenHidden: true,
      refreshInterval: stopRefresh ? undefined : interval,
      fallbackData: _study,
      errorRetryCount: 3,
      onSuccess: (study, key, config) => {
        if (!study) return

        const tot = study.status.runs_pending +
          study.status.runs_running +
          study.status.runs_failed +
          study.status.runs_completed +
          study.status.runs_cancelled
        
        if (tot === 1) {
          listRuns(projectOwner, projectName, [study.id], 1, 1)
            .then(d => {
              if (d.resources.length === 1) {
                setRun(d.resources[0])
              }
            })
            .catch(() => { setRun(undefined) })
            .finally(() => {
              sendJob(study)
              setIsReady(true)
            })
        } else {
          sendJob(study)
          setIsReady(true)
        }
        // stop fetching
        if (study?.status?.finished_at) setStopRefresh(true)
      }
    }
  )
  const sendJob = useCallback((job: CloudJob) => getValue(job), [study]) 

  const total = useMemo(() => {
    if (!study) return undefined

    return study.status.runs_pending +
      study.status.runs_running +
      study.status.runs_failed +
      study.status.runs_completed +
      study.status.runs_cancelled
  }, [study])

  const status: string = useMemo(() => {
    if (!study) return undefined

    if (study.status.status.toLocaleLowerCase() === 'completed') {
      if (study.status.runs_failed > 0) return 'failed'
      if (study.status.runs_cancelled > 0) return 'cancelled'
      if (study.status.runs_pending > 0) return 'pending'
    }
    return study.status.status.toLocaleLowerCase()
  }, [study])

  const [run, setRun] = useState<Run>()

  const [time, setTime] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const duration = useMemo(() => {
    if (!study || !time) return dayjs.duration({ seconds: 0 })
    return getDuration(study)
  }, [study, getDuration, time])

  /*
  * Manage hover 
  */

  const initialValues = useMemo(() => ({ 
    account: false,
    project: false,

    details: false,
    workspace: false,
    debug: false,

    detailsOver: false,
    workspaceOver: false,
    debugOver: false,

    author: false,
    recipe: false,
    info: false,

    settings: false
  }), [])

  const [hover, setHover] = useState<HoverObject>({ ...initialValues, [defaultTab]: true })
  
  const toggleHover = useCallback((id: string, value = false) => {
    setHover(prevHover => ({
      ...prevHover,
      [id]: value
    }))
  }, [setHover])

  /*
  * Reset hover and use default tab
  */
  useEffect(() => {
    setHover({ ...initialValues, [defaultTab]: true })
  }, [defaultTab, initialValues, setHover])

  return (
    <div className={(isReady && enableClick) ? `grid-container ${isReady && !study?.status?.finished_at && 'blink-effect'}` 
      : `grid-container disable-click ${isReady && !study?.status?.finished_at && 'blink-effect'}`}
    tabIndex={0}
    role='button'
    style={{
      borderColor: statusMap[status]?.color 
          ?? '#d0d7de',
      ...style
    }} 
    onClick={() => { 
      if (!enableClick) return

      if (study && total > 1) { 
        window.location.href = `/${projectOwner}/projects/${projectName}/studies/${study.id}?tab=details&perPage=5&status=null&page=1`
      } else if (study && run) {
        window.location.href = `/${projectOwner}/projects/${projectName}/studies/${study.id}/runs/${run.id}?tab=details`
      } else {
        // Do nothing...
      }}}
    >
      <Toaster />
      <div className='item1'>
        <a className='link'
          style={{ all: 'unset', cursor: 'pointer', marginRight: '0.25rem', 
            color: `${hover.account ? '#40a9ff' : '#000'}` }}
          title='Go to account page'
          onMouseOver={ (e) => toggleHover('account', true) }
          onMouseLeave={ (e) => toggleHover('account', false) }
          href={study && `/${study.owner.name}`}>
          {(projectOwner?.length > 20) 
            ? `${projectOwner.substring(0, 20)}...`
            : projectOwner}
        </a>
        <span style={{ marginRight: '0.25rem' }}>/</span>
        <a className='link' href={projectName && `/${projectOwner}/projects/${projectName}`}
          title='Go to project page'
          onMouseOver={ (e) => toggleHover('project', true) }
          onMouseLeave={ (e) => toggleHover('project', false) }
          style={hover.project ? { color: '#40a9ff' } : {}}>
          {projectName ?? '--'}
        </a>
      </div>
      
      <div className='item2'>
        <div>
          {(!isReady || !study) && <LoadingOutlined 
            style={{ margin: '0 10px 0 0' }}/>}
          
          {/* Single run - use link logic */}
          {(study && run && total === 1) && <>
            <span title='Go to run page'>
              <InfoCircleOutlined
                className='link'
                onClick={(e) => {
                  e.stopPropagation()
                  document.location = `/${projectOwner}/projects/${projectName}/studies/${study.id}/runs/${run.id}?tab=details`
                }}
                onMouseOver={(e) => {
                  toggleHover('detailsOver', true)
                }}
                onMouseLeave={(e) => {
                  toggleHover('detailsOver', false)
                }}
                style={{ marginRight: '0.75rem', 
                  color: `${(hover.details || hover.detailsOver) ? '#40a9ff' : '#000'}` }} />
            </span>

            <span title='Go to workspace page'>
              <FolderOutlined
                className='link'
                onClick={(e) => {
                  e.stopPropagation()
                  const trigger = getTrigger(run)
                  const q = trigger ? 'tab=workspace' : `tab=workspace&path=runs/${run.id}/workspace`

                  document.location = `/${projectOwner}/projects/${projectName}/studies/${study.id}/runs/${run.id}?${q}`
                }}
                onMouseOver={(e) => {
                  toggleHover('workspaceOver', true)
                }}
                onMouseLeave={(e) => {
                  toggleHover('workspaceOver', false)
                }}
                style={{ marginRight: '0.75rem', 
                  color: `${(hover.workspace || hover.workspaceOver) ? '#40a9ff' : '#000'}` }} />
            </span>
            
            <span title='Go to debug page'>
              <PartitionOutlined
                className='link'
                onClick={(e) => {
                  if (!study.status.finished_at) return

                  e.stopPropagation()
                  document.location = `/${projectOwner}/projects/${projectName}/studies/${study.id}/runs/${run.id}?tab=debug`
                }}
                onMouseOver={(e) => {
                  toggleHover('debugOver', true)
                }}
                onMouseLeave={(e) => {
                  toggleHover('debugOver', false)
                }}
                style={{ marginRight: `${canWrite
                  ? '0.75rem' : '0'}`,  
                color: `${(!study.status.finished_at) ? 'gray'
                  : (hover.debug || hover.debugOver) ? '#40a9ff' 
                    : '#000'}` }} />
            </span>
          </>}

          {/* Study - use tab logic */}
          {(study && total > 1) && <>
            <span title='Go to study page'>
              <InfoCircleOutlined
                className='link'
                onClick={(e) => {
                  e.stopPropagation()
                  if (enableLink) {
                    document.location = `/${projectOwner}/projects/${projectName}/studies/${study.id}?tab=details`
                  } else {
                    getTab(JobTabs.details)
                  
                    toggleHover(JobTabs.workspace, false)
                    toggleHover(JobTabs.details, true)
                  }
                }}
                onMouseOver={(e) => {
                  toggleHover('detailsOver', true)
                }}
                onMouseLeave={(e) => {
                  toggleHover('detailsOver', false)
                }}
                style={{ marginRight: '0.75rem', 
                  color: `${(hover.details || hover.detailsOver) 
                    ? '#40a9ff' : '#000'}` }} />
            </span>
          
            <span title='Go to workspace page'>
              <FolderOutlined
                className='link'
                onClick={(e) => {
                  e.stopPropagation()
                  if (enableLink) {
                    document.location = `/${projectOwner}/projects/${projectName}/studies/${study.id}?tab=workspace`
                  } else {
                    getTab(JobTabs.workspace)

                    toggleHover(JobTabs.workspace, true)
                    toggleHover(JobTabs.details, false)
                  }
                }}
                onMouseOver={(e) => {
                  toggleHover('workspaceOver', true)
                }}
                onMouseLeave={(e) => {
                  toggleHover('workspaceOver', false)
                }}
                style={{ marginRight: `${canWrite
                  ? '0.75rem' : '0'}`,  
                color: `${(hover.workspace || hover.workspaceOver)
                  ? '#40a9ff' : '#000'}` }} />
            </span>
          </>}

          {canWrite && <>
            <span title='See study actions'>
              <SettingOutlined
                className='link'
                onMouseOver={(e) => toggleHover('settings', true)}
                onMouseLeave={(e) => toggleHover('settings', false)}
                onClick={(e) => {
                  e.stopPropagation()
                  setSeeAction(prev => !prev)
                  setSeeDescription(false)
                }}
                style={{
                  cursor: 'pointer',
                  color: `${(hover.settings) 
                    ? '#40a9ff' : '#000'}` }} />
            </span>
          </>}
        </div>
      </div>

      {/* Study details */}
      <div className='item3'>
        <button style={{ all: 'unset', margin: '0 8px 0 0', cursor: 'pointer' }}
          title='See study info'
          onMouseOver={(e) => toggleHover('info', true)}
          onMouseLeave={(e) => toggleHover('info', false)}
          onClick={(e) => {
            e.stopPropagation()
            setSeeDescription(prev => !prev)
            setSeeAction(false)
          }}>
          <InfoCircle size={14} style={hover.info ? { color: '#40a9ff' } : {}}/>
        </button>
        <span>
          {study ? study.spec.name ?? `Study: ${study.id}` : '--'}
          {study && total > 1 && `(#${total})`}
        </span>
      </div>

      <div className='item4' title={study && study?.status?.finished_at && dayjs(study.status.started_at).utc(true).tz(tmz).format('[on] MMM DD YYYY [at] HH:mm')}>
        {study ? dayjs(study.status.started_at).utc(true).tz(tmz).format('[on] MMM DD YYYY') : '--'}
      </div>

      <div className='item5'>
        <span style={{ marginRight: '0.75rem' }}>
          <Avatar 
            src={study ? study.author?.picture_url : ''} 
            size={24} 
            color = {hover.author ? '#40a9ff' : undefined} />
        </span>
        <a className='link' href={study ? `/${study.author?.name}` : ''} 
          title='Go to author page'
          rel='noreferrer'
          onMouseOver={ (e) => toggleHover('author', true) }
          onMouseLeave={ (e) => toggleHover('author', false) }
          style={hover.author ? { color: '#40a9ff' } : {}}>
          {study ? (study.author?.display_name ?? study.author?.name) : '--'}
        </a>
      </div>

      <div className='item6'>
        <span style={{ marginRight: '0.75rem' }}>
          <Avatar 
            src={study ? study.recipe?.metadata?.icon : ''} 
            size={24} 
            color = {hover.recipe ? '#40a9ff' : undefined} />
        </span>
        <a className='link' 
          href={study ? recipeLinkFromSource(study?.recipe?.source) : ''}
          title='Go to recipe page'
          rel='noreferrer'
          onMouseOver={ (e) => toggleHover('recipe', true) }
          onMouseLeave={ (e) => toggleHover('recipe', false) }
          style={hover.recipe ? { color: '#40a9ff' } : {}}>
          {study ? study.recipe?.metadata?.name : '--'}
        </a>
        {study && <span className='light-text'> {study.recipe?.metadata?.tag}</span>}
      </div>

      <div className='item8'>
        <span className='status-label' style={{ marginRight: '0.75rem' }}>
          {study ? status.charAt(0).toUpperCase() + status.slice(1) : '--'}
        </span>
        <span className='light-text'>
          {!isReady && <LoadingOutlined />}
          {(study && total == 1) && run && width > 600 &&
            `${run.meta?.progress?.completed} / ${run.meta?.progress?.total} steps`}
          {(study && total > 1) && width > 600 &&
            `${study.status?.runs_completed} / ${total} runs`}
        </span>
      </div>

      <div className='item9' title='CPU usage'>
        <Cpu size={18} style={{ marginRight: '0.15rem' }} />
        <span className='light-text'>
          {study ? `${formatDuration(dayjs.duration(study.resources_duration.cpu, 'seconds'))}` : '--'}
        </span>
      </div>

      <div className='item10' title='Duration of the simulation'>
        <Clock size={18} style={{ marginRight: '0.15rem' }} />
        <span className='light-text'>
          {study ? `${formatDuration(duration)}` : '--'}
        </span>
      </div>

      {seeDescription && <>
        <div className='item7'>
          {study ? study.spec.description : '--'}
        </div>
      </>}

      {seeAction && width && <>
        <div className='item7'
          style={{ display:'flex' }}
        >
          <Button 
            style={{ marginRight: '0.75rem' }}
            disabled={!study || (![JobStatusEnum.Cancelled, JobStatusEnum.Completed].includes(study.status.status))}
            onClick={(e) => {
              e.stopPropagation()
              if (!study) return

              toast.promise(
                deleteJob(projectOwner, projectName, study.id),
                {
                  loading: 'Deleting study...',
                  success: 'Study deleted',
                  error: (err) => {
                    console.log(err)
                    return `Failed to delete study: ${err}`
                  }
                },
                {
                  style: { minWidth: '300px', fontSize: '12px' },
                  success: {
                    duration: 5000,
                    icon: '🔥',
                    position: 'bottom-left'
                  },
                  error: {
                    duration: 5000,
                    position: 'bottom-left'
                  },
                  loading: {
                    position: 'bottom-left'
                  }
                }
              ).finally(() => {
                mutate()
              })
            }}
          >
            <Trash style={{ display: 'inline', margin: '0 5px 0 0' }} /> Delete
          </Button>

          <Button 
            style={{ marginRight: '0.75rem' }}
            disabled={!study || (study.status.runs_pending < 1 && study.status.runs_running < 1)}
            onClick={(e) => {
              e.stopPropagation()
              if (!study) return

              toast.promise(
                cancelJob(projectOwner, projectName, study.id),
                {
                  loading: 'Cancelling study...',
                  success: 'Study cancelled',
                  error: (err) => {
                    console.log(err)
                    return `Failed to cancel study: ${err}`
                  }
                },
                {
                  style: { minWidth: '300px', fontSize: '12px' },
                  success: {
                    duration: 5000,
                    icon: '🔥',
                    position: 'bottom-left'
                  },
                  error: {
                    duration: 5000,
                    position: 'bottom-left'
                  },
                  loading: {
                    position: 'bottom-left'
                  }
                }
              ).finally(() => {
                mutate()
              })
            }}
          >
            <XOctagon style={{ display: 'inline', margin: '0 5px 0 0' }} /> Cancel
          </Button>
          <Button 
            style={{ marginRight: '0.75rem' }}
            disabled={!study || (study.status.runs_cancelled == 0 && study.status.runs_failed == 0)}
            loading={retryLoading}
            onClick={(e) => {
              setRetryLoading(true)
              e.stopPropagation()
              if (!study) return

              toast.promise(
                retryFailedRuns(projectOwner, projectName, study.id),
                {
                  loading: 'Retrying failed runs...',
                  success: 'Retried failed runs',
                  error: (err) => {
                    console.log(err)
                    return `Failed to retry failed run: ${err}`
                  }
                },
                {
                  style: { minWidth: '300px', fontSize: '12px' },
                  success: {
                    duration: 5000,
                    icon: '🔥',
                    position: 'bottom-left'
                  },
                  error: {
                    duration: 5000,
                    position: 'bottom-left'
                  },
                  loading: {
                    position: 'bottom-left'
                  }
                }
              ).finally(() => {
                mutate()
                setRetryLoading(false)
              })
            }}
          >
            <Recycle style={{ display: 'inline', margin: '0 5px 0 0' }} /> Retry Failed Runs
          </Button>
        </div>
      </>}
    </div>
  )
}

export default StudyCard
